import { BorderRadius, Color, Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon } from '@persona-id/design-tokens/icons/16px';
import { meanBy, startCase } from 'lodash';
import { DateTime, Duration } from 'luxon';
import { ComponentProps, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { Period } from 'dashboard/lib/unixTime';
import { fillChartData } from 'dashboard/lib/utils/charting';
import { formatChartDate, formatChartDateLongWithDay } from 'dashboard/lib/utils/formatters';
import {
  Inquiry,
  InquiryBehaviorThreatLevel,
  InquiryRiskDistribution,
  InquiryRiskDistributionDatum,
  InquiryRiskModelLabel,
} from 'dashboard/models/inquiry';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Stack from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import PDSTooltip from 'design-system/components/Tooltip';
import {
  DATE_AND_TIME_FORMAT,
  HOURS_ELAPSED_FORMAT,
  MINUTES_SECONDS_ELAPSED_FORMAT,
  SHORT_FORM_DATE,
} from 'lib/constants/luxonFormats';
import useApiCallback from 'lib/hooks/useApiCallback';
import { ApiEndpoint } from 'lib/interfaces/api';
import { captureMessage } from 'lib/reporting';
import { assertNever } from 'lib/utils/types';

const UpperSectionContainer = styled('div')`
  display: grid;
  grid-template-columns: 20% 80%;
  min-height: 100px;
  border-radius: ${BorderRadius.Md};
  border: 1px solid ${Color.CoreSlate100};
  margin-bottom: ${Spacing[5]};
`;

const UpperSectionLeftPane = styled('div')`
  grid-column-start: 1;
  grid-column-end: 2;
  border-right: 1px solid ${Color.CoreSlate100};
  padding: ${Spacing[4]};
`;

const UpperSectionRightPane = styled('div')`
  grid-column-start: 2;
  padding: ${Spacing[4]};
`;

const SummaryBoxesContainer = styled('div')`
  display: flex;
  justify-content: left;
  margin: ${Spacing[2]} 0;
`;

const SummaryBox = styled('div')`
  border: 1px solid ${Color.CoreSlate100};
  border-radius: ${BorderRadius.Sm};
  width: 200px;
  padding: ${Spacing[4]};
  margin-right: ${Spacing[2]};
`;

const StyledSticker = styled(Sticker)`
  margin-left: ${Spacing[2]};
`;

const StyledOverflowSpan = styled('span')`
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export interface InquiryRiskDistributionResponse {
  meta: InquiryRiskDistribution;
}

interface InquiryRiskModelProps {
  inquiry: Inquiry;
}

const InquiryRiskModel = ({ inquiry }: InquiryRiskModelProps) => {
  const [chartData, setChartData] = useState<InquiryRiskDistributionDatum[]>([]);

  const { loading, callback: loadRiskDistribution } = useApiCallback(async () => {
    try {
      const riskDistributionResponse =
        await SessionClientDashboard.getEndpoint<InquiryRiskDistributionResponse>(
          ApiEndpoint.MetricsInquiriesRiskDistribution,
          {
            filter: {
              'date-range-end':
                inquiry.attributes['completed-at'] || inquiry.attributes['created-at'],
            },
          },
        );

      const filledChartData = fillChartData(
        riskDistributionResponse.meta.data,
        {} as Omit<InquiryRiskDistributionDatum, 'created_timestamp'>, // we don't want to render dots without data, so no default values
        [
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          DateTime.fromISO(inquiry.attributes['completed-at'])
            .startOf('day')
            .minus({ days: 7 })
            .toSeconds(),
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          DateTime.fromISO(inquiry.attributes['completed-at']).toSeconds(),
        ],
        Period.Day,
        false,
        false,
      );

      setChartData(filledChartData);
    } catch {
      captureMessage('Unable to load inquiry risk distribution from snowflake', 'warning', {
        inquiry: inquiry.id,
      });
      setChartData([]);
    }
  }, [inquiry]);

  const {
    'completed-at': completedAt,
    'request-spoof-attempts': requestSpoofAttempts,
    'user-agent-spoof-attempts': userAgentSpoofAttempts,
    'distraction-events': distractionEvents,
    'shortcut-copies': shortcutCopies,
    'shortcut-pastes': shortcutPastes,
    'autofill-starts': autofillStarts,
    'completion-time': completionTime,
    'hesitation-percentage': hesitationPercentage,
    'behavior-threat-level': behaviorThreatLevel,

    'risk-model-response': riskModelResponse,
  } = inquiry.attributes;

  if (inquiry.relationships.sessions.data.length > 0) {
    let threatLevelStickerVariant: ComponentProps<typeof Sticker>['variant'];

    switch (behaviorThreatLevel) {
      case InquiryBehaviorThreatLevel.Low:
        threatLevelStickerVariant = 'success';
        break;
      case InquiryBehaviorThreatLevel.Medium:
        threatLevelStickerVariant = 'caution';
        break;
      case InquiryBehaviorThreatLevel.High:
        threatLevelStickerVariant = 'danger';
        break;
      case undefined:
      case null:
        threatLevelStickerVariant = 'caution';
        break;
      default:
        assertNever(behaviorThreatLevel);
    }

    // Handle the completedAt information
    let completedAtDayFormatted;
    let completedAtTimeFormatted;
    let dateRangeStartFormatted;
    if (completedAt) {
      completedAtDayFormatted = `${DateTime.fromISO(completedAt).toFormat(SHORT_FORM_DATE)}`;
      completedAtTimeFormatted = `${DateTime.fromISO(completedAt).toFormat(DATE_AND_TIME_FORMAT)}`;
      dateRangeStartFormatted = `${DateTime.fromISO(completedAt)
        .minus({ days: 7 })
        .toFormat(SHORT_FORM_DATE)}`;
    }

    let completionTimeFormatted;
    if (completionTime) {
      completionTimeFormatted =
        completionTime >= 60 * 60
          ? Duration.fromObject({ seconds: completionTime }).toFormat(HOURS_ELAPSED_FORMAT)
          : Duration.fromObject({ seconds: completionTime }).toFormat(
              MINUTES_SECONDS_ELAPSED_FORMAT,
            );
    }

    const ipAddress = riskModelResponse?.features?.['ip-burst']?.['ip-address'] || '';

    return (
      <Card>
        <Card.Body>
          <Stack direction='horizontal' align='start' justify='space-between' spacing={Spacing[3]}>
            <Card.Title>Behavioral Risk Signals</Card.Title>
            <Button
              id='inquiry-risk-model__run-query'
              data-test='inquiry-risk-model__run-query'
              onClick={loadRiskDistribution}
              isPending={loading}
              disabled={loading}
              size='sm'
              width='auto'
            >
              Run Query
            </Button>
          </Stack>
          {riskModelResponse && (
            <UpperSectionContainer>
              <UpperSectionLeftPane>
                <Label size='lg' weight='bold'>
                  Risk Level
                </Label>
                <InquiryRiskLabelSticker label={riskModelResponse.label} />
                <Spacer height={2} />
                <AttributeGrid colCount={1}>
                  <AttributeItem
                    title='IP Address'
                    value={
                      <PDSTooltip
                        disabled={ipAddress.length < 19}
                        trigger={<StyledOverflowSpan>{ipAddress}</StyledOverflowSpan>}
                      >
                        {ipAddress}
                      </PDSTooltip>
                    }
                    helpMessage={
                      ipAddress &&
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      (riskModelResponse.features['ip-burst']?.count > 0
                        ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          `The IP address associated with this inquiry was found on ${riskModelResponse.features['ip-burst'].count} other recent inquiries.`
                        : 'The IP address associated with this inquiry was not found on any other recent inquiries.')
                    }
                    width='100%'
                  />
                  <AttributeItem title='Completed At' value={completedAtTimeFormatted} />
                </AttributeGrid>
                <Label size='lg' weight='bold'></Label>
              </UpperSectionLeftPane>
              <UpperSectionRightPane>
                <Label size='lg' weight='bold'>
                  How this compares to other inquiries
                </Label>
                <Spacer height={2} />
                <Paragraph size='md'>
                  <Label size='md' color='secondary' weight='bold'>
                    {dateRangeStartFormatted}
                  </Label>{' '}
                  -{' '}
                  <Label size='md' color='secondary' weight='bold'>
                    {completedAtDayFormatted}
                  </Label>
                </Paragraph>
                <DistributionSummary data={chartData} currentLabel={riskModelResponse.label} />
                <Spacer height={4} />
                <ResponsiveContainer height={250} className='chromatic-ignore'>
                  <LineChart data={chartData}>
                    <CartesianGrid vertical={false} />
                    <Line
                      type='linear'
                      dataKey={(entry) => (entry['low'] * 100).toFixed(1)}
                      name='low'
                      stroke={Color.PrimaryGreen}
                      strokeWidth={2}
                      dot={{
                        stroke: Color.PrimaryGreen,
                        strokeWidth: 2,
                        r: 3,
                        fill: Color.PrimaryGreen,
                      }}
                    />
                    <Line
                      type='linear'
                      dataKey={(entry) => (entry['medium'] * 100).toFixed(1)}
                      name='medium'
                      stroke={Color.PrimaryYellow}
                      strokeWidth={2}
                      dot={{
                        stroke: Color.PrimaryYellow,
                        strokeWidth: 2,
                        r: 3,
                        fill: Color.PrimaryYellow,
                      }}
                    />
                    <Line
                      type='linear'
                      dataKey={(entry) => (entry['high'] * 100).toFixed(1)}
                      name='high'
                      stroke={Color.PrimaryRed}
                      strokeWidth={2}
                      dot={{
                        stroke: Color.PrimaryRed,
                        strokeWidth: 2,
                        r: 3,
                        fill: Color.PrimaryRed,
                      }}
                    />
                    <XAxis
                      dataKey='created_timestamp'
                      // In the event that we don't have any data, we'll default to showing 12/31. That will be the
                      // null state.
                      tickFormatter={(t) => (typeof t === 'number' ? formatChartDate(t) : '12/31')}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis ticks={[25, 50, 75, 100]} unit='%' axisLine={false} tickLine={false} />
                    <Tooltip
                      labelFormatter={(t) => formatChartDateLongWithDay(t)}
                      formatter={(value, name, _) => [
                        isNaN(value as number) ? '-' : `${value}%`,
                        startCase(name as string),
                      ]}
                    />
                    <Legend formatter={startCase} />
                  </LineChart>
                </ResponsiveContainer>
              </UpperSectionRightPane>
            </UpperSectionContainer>
          )}

          <Card.SectionHeader>Spoof attempts</Card.SectionHeader>
          <AttributeGrid>
            <AttributeItem
              title='Behavior threat level'
              value={
                behaviorThreatLevel && (
                  <Sticker variant={threatLevelStickerVariant}>
                    {behaviorThreatLevel.toUpperCase()}
                  </Sticker>
                )
              }
              helpMessage='A composite score assessing overall fraud risk from spoof attempts.'
            />
            <AttributeItem
              title='Request spoof attempts'
              value={
                <>
                  {requestSpoofAttempts?.toString()}{' '}
                  {requestSpoofAttempts != null && requestSpoofAttempts > 0 && (
                    <CircleAlertIcon color={Color.CoreRed800} />
                  )}
                </>
              }
              helpMessage='Number of requests that were likely spoofed.'
            />
            <AttributeItem
              title='User agent spoof attempts'
              value={
                <>
                  {userAgentSpoofAttempts?.toString()}{' '}
                  {userAgentSpoofAttempts != null && userAgentSpoofAttempts > 0 && (
                    <CircleAlertIcon color={Color.CoreRed800} />
                  )}
                </>
              }
              helpMessage='Number of user agent headers that were likely spoofed on seen devices.'
            />
          </AttributeGrid>

          <Spacer height={2} />

          <Card.SectionHeader>User behavior</Card.SectionHeader>
          <AttributeGrid>
            <AttributeItem
              title='User completion time'
              value={completionTimeFormatted}
              helpMessage='Time duration of user interaction.'
            />
            <AttributeItem
              title='Distraction events'
              value={distractionEvents?.toString()}
              helpMessage='Number of times the user left the flow.'
            />
            <AttributeItem
              title='Hesitation percentage'
              value={hesitationPercentage && `${hesitationPercentage?.toFixed(3)}%`}
              helpMessage='Percentage of time in the flow where the user made no inputs.'
            />
          </AttributeGrid>

          <Spacer height={2} />

          <Card.SectionHeader>Form filling</Card.SectionHeader>
          <AttributeGrid>
            <AttributeItem
              title='Shortcut usage (copies)'
              value={shortcutCopies?.toString()}
              helpMessage='Number of times the user used a keyboard shortcut to copy information.'
            />
            <AttributeItem
              title='Shortcut usage (pastes)'
              value={shortcutPastes}
              helpMessage='Number of times the user used a keyboard shortcut to paste information.'
            />
            <AttributeItem
              title='Autofill starts'
              value={autofillStarts?.toString()}
              helpMessage='Number of times the user autofilled a form field.'
            />
          </AttributeGrid>
        </Card.Body>
      </Card>
    );
  } else {
    return (
      <Card>
        <Card.Body>
          <Card.Title>Behavioral Risk Signals</Card.Title>
          <Spacer height={1.5} width={2.5} />
          <TableNullState dataTestBlock='inquiry-behavior-card'>
            <Heading size='sm' data-test='inquiry-behavior-card__missing'>
              Behavioral risk is unavailable for this inquiry.
            </Heading>
          </TableNullState>
        </Card.Body>
      </Card>
    );
  }
};

const InquiryRiskLabelSticker = ({ label }: { label: InquiryRiskModelLabel }) => {
  switch (label) {
    case InquiryRiskModelLabel.High:
      return <StyledSticker variant='danger'>{label.toUpperCase()}</StyledSticker>;
    case InquiryRiskModelLabel.Medium:
      return <StyledSticker variant='caution'>{label.toUpperCase()}</StyledSticker>;
    case InquiryRiskModelLabel.Low:
      return <StyledSticker variant='success'>{label.toUpperCase()}</StyledSticker>;
    default:
      return null;
  }
};

const DistributionSummary = ({
  data,
  currentLabel,
}: {
  data: InquiryRiskDistributionDatum[];
  currentLabel: InquiryRiskModelLabel;
}) => (
  <SummaryBoxesContainer>
    {Object.values(InquiryRiskModelLabel).map((label) => {
      const labelMean = meanBy(
        data.filter((d) => label in d),
        label,
      );
      return (
        <SummaryBox key={label}>
          <AttributeItem
            title={startCase(label)}
            textTransform='sentence'
            value={isNaN(labelMean) ? '-' : `${(labelMean * 100).toFixed(1)}%`}
          />
          {label === currentLabel && (
            <>
              <Spacer height={0.5} />
              <Paragraph size='xs' color='tertiary'>
                This inquiry&apos;s risk level
              </Paragraph>
            </>
          )}
        </SummaryBox>
      );
    })}
  </SummaryBoxesContainer>
);

export default InquiryRiskModel;
